/**
 * Copyright Mántum All Rights Reserved.
 */
import Input from '../input';
import { validator } from '@mantum/kanela';

import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
    useState
} from 'react';

import type { Constraint, Validation, Detail } from '@mantum/kanela';

export type PropTypes = {
    disabled?: boolean;
    name?: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (name?: string, value?: unknown) => void;
    optional?: boolean;
    placeholder?: string;
    testid?: string;
    validation?: {
        email?: Constraint;
    } & Validation;
    value?: string;
};

export default forwardRef<Detail<unknown>, PropTypes>(
    function Email(props, ref) {
        // prettier-ignore
        const {
        optional = true
    } = props;

        let enabled = props.validation?.email?.enabled;
        if (validator.isUndefined(enabled)) {
            enabled = true;
        }

        const [error, setError] = useState(false);
        const validationRef = useRef<Detail<string>>(null);

        const validate = useCallback(
            (value?: string): boolean => {
                let result = false;
                if (validator.isEmpty(value)) {
                    setError(false);
                    return result;
                }

                if (enabled) {
                    result = validator.isEmail(value);
                    setError(!result);
                }

                return result;
            },
            [enabled]
        );

        useImperativeHandle(
            ref,
            () => {
                return {
                    name: validationRef.current?.name,
                    validable: true,
                    clear() {
                        validationRef.current?.clear();
                    },
                    validate() {
                        if (!validationRef.current?.validate()) {
                            return false;
                        }

                        const value = validationRef.current?.value();
                        if (value) {
                            return validate(value);
                        }

                        return true;
                    },
                    value() {
                        return validationRef.current?.value();
                    }
                };
            },
            [validate]
        );

        const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
            const value = event.target.value;
            if (!validate(value)) {
                return;
            }

            if (props.onBlur) {
                props.onBlur(event);
            }
        };

        const message = error
            ? props.validation?.email?.message
            : props.validation?.message;

        return (
            <Input
                ref={validationRef}
                type='email'
                autocomplete='email'
                disabled={props.disabled}
                error={error}
                name={props.name}
                onBlur={handleBlur}
                message={message}
                onChange={props.onChange}
                optional={optional}
                placeholder={props.placeholder}
                testid={props.testid}
                value={props.value}
            />
        );
    }
);
